import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import { SessionContext } from "../../utility/useSession";
import DashPage from "../dash/DashPage";
import LocationPickerDialog from "../../components/pickers/locationPicker/LocationPickerDialog";
import { AssetLocationTypes, ReportParameterTypes } from "../../logic/api";
import NotFoundPage from "../error/NotFoundPage";
import { isSysAdmin } from "../../utility/utils";
import { useHistory } from "react-router-dom";
import usePaths from "../../utility/paths";
import { ReleaseFlag, useReleaseManagement } from "../../utility/feature-management";

// Version control: This map controls which version of reports is loaded.
// The first released flag wins.
const releaseMap: Record<string, ReleaseFlag> = {
  "PKI-8338": ReleaseFlag.releaseReports8338,
  "PKI-8630": ReleaseFlag.releaseReports8630,
  "PKI-8764": ReleaseFlag.releaseReports8764,
  "PKI-8839": ReleaseFlag.releaseReports8839,
  "PKI-9066": ReleaseFlag.releaseReports9066,
  "PKI-8666": ReleaseFlag.releaseReports8666,
  "PKI-9056": ReleaseFlag.releaseReports9056,
  "PKI-9094": ReleaseFlag.releaseReports9094,
  "PKI-9145": ReleaseFlag.releaseReports9145,
  "PKI-9354": ReleaseFlag.releaseReports9354,
  "PKI-8841": ReleaseFlag.releaseReports8841,
  "PARK-10556": ReleaseFlag.releaseReports10556,
  "EXPD-171": ReleaseFlag.releaseReportsExPD171,
  //New release go here.
};

const getLatestReleasedVersion = () => {
  const pkis = Object.keys(releaseMap).reverse(); //Runs from bottom to top.
  for (const pki of pkis) {
    const releaseFlag = releaseMap[pki];
    const isReleased = useReleaseManagement(releaseFlag);
    if (isReleased) {
      return pki;
    }
  }
  return "";
}

const ReportsExPDPage: React.FC = () => {
  const session = useContext(SessionContext);
  const token =
    (session?.current?.context?.headers["authorization"] ?? "").split(" ")[1] ?? "";
  const releaseParkITReportsDomain = useReleaseManagement(ReleaseFlag.releaseReportsParkITDomain);

  const releaseCode = getLatestReleasedVersion();

  let baseUrl = "https://reports.expd.co.uk";

  if (releaseParkITReportsDomain) {
    baseUrl = "https://reports.parkit.cloud";
  }

  if (window.location.host.indexOf("localhost") !== -1) {
    baseUrl = "http://localhost:54524";
  } else if (releaseCode.length > 0) {
    baseUrl = [baseUrl, "v", releaseCode].join("/")
  }

  const iframeRef = useRef<HTMLIFrameElement>(null);

  return (
    <DashPage noScroll={true}>
      <div
        style={{
          position: "absolute",
          left: "0px",
          right: "0px",
          top: "0px",
          bottom: "0px",
        }}
      >
        <ReportIframeComponents iframeRef={iframeRef} />
        <IFrameComponent
          key="reportsframe"
          ref={iframeRef}
          src={`${baseUrl}/reports/${token}?u=${encodeURIComponent(window.location.href)}&d=${Date.now()}&timezone=${encodeURIComponent(
            session.current.site?.preferences?.time_zone || ""
          )}`}
        />

      </div>
    </DashPage>
  );
};

interface ReportIframeComponentsProps {
  iframeRef: React.RefObject<HTMLIFrameElement>;
}

const ReportIframeComponents: React.FC<ReportIframeComponentsProps> = ({
  iframeRef,
}) => {
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const [eventData, setEventData] = useState<Record<string, unknown>>();
  const session = useContext(SessionContext);
  const paths = usePaths();
  const history = useHistory();


  if (session.current.site == null) {
    return <Fragment></Fragment>;
  }

  if (session.current.site.preferences.reports_v2 == false && isSysAdmin(session) == false) {
    return <NotFoundPage title={"Not available"} message={"Feature not enabled"}></NotFoundPage>
  }



  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      if (event.data.action === "showPicker") {
        setEventData(event.data);
        setShowPicker(true);
      } else if (event.data.action === "nav_home") {
        history.push(paths.home);
      } else if (event.data.action === "nav_reports") {
        window.history.replaceState(null, '', paths.reportsV2);
      } else if (event.data.action === "nav_category") {
        window.history.replaceState(null, '', paths.reportsV2WithCategory(event.data.category));
      } else if (event.data.action === "nav_report") {
        window.history.replaceState(null, '', paths.reportV2View(event.data.reportId, false));
      } else if (event.data.action === "nav_edit") {
        history.push(paths.reportEdit(event.data.reportId));//Need to use reports V1 for editing MySQL.
      }
    };
    window.addEventListener("message", messageHandler);
    return () => {
      window.removeEventListener("message", messageHandler);
    };
  }, []);

  const inputMapping: Record<number, {
    selectionTypes: AssetLocationTypes[];
    displayTypes: AssetLocationTypes[];
  }> = {
    [ReportParameterTypes.locationZone]: {
      displayTypes: [AssetLocationTypes.Zone],
      selectionTypes: [AssetLocationTypes.Zone],
    },
    [ReportParameterTypes.locationLane]: {

      displayTypes: [AssetLocationTypes.Lane, AssetLocationTypes.Zone],
      selectionTypes: [AssetLocationTypes.Lane],
    },
    [ReportParameterTypes.locationZoneOrLane]: {
      displayTypes: [AssetLocationTypes.Lane, AssetLocationTypes.Zone],
      selectionTypes: [AssetLocationTypes.Lane, AssetLocationTypes.Zone],
    },
    [ReportParameterTypes.locationKeyroom]: {
      displayTypes: [AssetLocationTypes.Keyroom],
      selectionTypes: [AssetLocationTypes.Keyroom],
    },
    [ReportParameterTypes.locationHook]: {
      displayTypes: [AssetLocationTypes.Hook, AssetLocationTypes.Keyroom],
      selectionTypes: [AssetLocationTypes.Hook],
    },
    [ReportParameterTypes.locationKeyLocker]: {
      displayTypes: [AssetLocationTypes.KeyLocker],
      selectionTypes: [AssetLocationTypes.KeyLocker],
    },
    [ReportParameterTypes.locationKeyLockerBox]: {
      displayTypes: [AssetLocationTypes.KeyLockerBox, AssetLocationTypes.KeyLocker],
      selectionTypes: [AssetLocationTypes.KeyLockerBox],
    },
    [ReportParameterTypes.locationUser]: {
      displayTypes: [AssetLocationTypes.User, AssetLocationTypes.Users],
      selectionTypes: [AssetLocationTypes.User],
    },
  };

  const inputType = eventData?.type as AssetLocationTypes;
  const mapping = (inputMapping[inputType] ?? {});
  const displayTypes = mapping.displayTypes ?? [];
  const selectionTypes = mapping.selectionTypes ?? [];

  return (
    <Fragment>
      <LocationPickerDialog
        title={(eventData?.alias ?? "Location").toString()}
        show={showPicker}
        displayTypes={displayTypes}
        selectionTypes={selectionTypes}
        onChange={(location) => {
          iframeRef.current?.contentWindow?.postMessage(
            {
              type: "picked",
              data: location,
            },
            "*"
          );
          setShowPicker(false);
        }}
      />
    </Fragment>
  );
};

const IFrameComponent = React.forwardRef<HTMLIFrameElement, { src: string }>(
  ({ src }, ref) => (
    <iframe ref={ref} frameBorder="0" width="100%" height="100%" src={src}></iframe>
  )
);

IFrameComponent.displayName = "IFrameComponent";

export default ReportsExPDPage;
